import {
    BodyText,
    Checkbox,
    Flex,
    IconV2,
    LayoutBox,
    TextButton,
} from '@bamboohr/fabric';
import { useState, useMemo } from 'react';

import { isEnabled } from 'FeatureToggle.util';

import '../state-withholding.react/state-withholding.styl';
import { AdditionalWithholding } from '../additional-withholding.react';
import { LinkedSelects } from '../linked-selects.react';
import { OptionalTextInput } from '../optional-text-input.react';
import { OptionalCheckbox } from '../optional-checkbox.react';
import { OptionalSelect } from '../optional-select.react';
import { OptionalUsdInput } from '../optional-usd-input/optional-usd-input';
import { SectionStyledBox } from '../section-styled-box';
import { SectionStyledHeader } from '../section-styled-header';
import { LocationSelect } from './location-select';
import { FilingStatusSelect } from './filing-status-select';
import { OptionalFields } from './optional-fields';
import { Exemptions } from './exemptions';
import type { StateTaxWithholdingData } from '../../types';

const feAccessLevelsQuickForms = isEnabled('PAY_INFO_TAB_ACCESS_LEVELS');

/**
 * Props for the StateWithholdingLA component
 */
export type StateWithholdingLAProps = {
    accessLevel?: string | number;
    editPermission?: boolean;
    headerHeadlineSize?: 'small' | 'medium';
    isAdminUser?: boolean;
    isPayrollAdminUser?: boolean;
    newHirePacket?: boolean;
    onLocationChange?: (location: {locationVal: string}) => void;
    sectionNote?: string; 
    selectedLocation?: string;
    showLocationRequiredAsterisk?: boolean;
    showWithholding?: boolean;
    usesSectionComponent?: boolean;
    viewOnly?: boolean;
    stateInfo?: StateTaxWithholdingData;
};

/**
 * Map of optional field types to their corresponding components
 */
const OPTIONAL_FIELDS_MAP = {
    text: OptionalTextInput,
    checkbox: OptionalCheckbox,
    select: OptionalSelect,
    linkedSelects: LinkedSelects,
    usd: OptionalUsdInput,
} satisfies Record<string, React.ComponentType<any>>;

export type OptionalFieldTypes = typeof OPTIONAL_FIELDS_MAP;

/**
 * StateWithholdingLA Component
 * 
 * A functional component for handling Louisiana state tax withholding.
 * This component allows users to select their filing status, manage exemptions,
 * and configure additional withholding options for Louisiana state taxes.
 * 
 * This component and its subcomponents are a rewrite of iowa-state-withholding.js as a functional component
 * in TS. We can look to this component as a template for future form migrations, although this component and
 * its subcomponents may first need some cleaning up.
 */
export function StateWithholdingLA({
    accessLevel,
    editPermission = false,
    headerHeadlineSize = 'medium',
    isAdminUser = false,
    isPayrollAdminUser = false,
    newHirePacket = false,
    onLocationChange,
    sectionNote,
    selectedLocation,
    showLocationRequiredAsterisk = false,
    showWithholding = true,
    usesSectionComponent = false,
    viewOnly = false,
    stateInfo,
}: StateWithholdingLAProps) {
    
    // Get current active location based on selected location
    const activeLocation = selectedLocation ? stateInfo.locations.byId[selectedLocation] : null;
    
    // Initialize state values
    const initialFilingStatus = activeLocation?.newFields.filingStatus 
        ? activeLocation.newFields.filingStatus.find(status => status.selected === true) || null
        : null;
    const initialIgnore = activeLocation?.newFields.ignoreStatusExemptions?.checked || false;
    const stateAbbr = activeLocation?.state || null;
    
    // State hooks
    const [filingStatus, setFilingStatus] = useState(initialFilingStatus);
    const [ignore, setIgnore] = useState(initialIgnore);
    
    // Handle location change
    const handleLocationChange = (selectedValues: string[]) => {
        const [selectedValue = ''] = selectedValues;
        
        onLocationChange?.({
            locationVal: selectedValue,
        });
    };
    
    // Memoize optional fields to avoid unnecessary re-renders
    const optionalFields = useMemo(() => {
        if (!activeLocation || !activeLocation.newFields.optionalFields) {
            return null;
        }
        
        return (
            <OptionalFields
                biIdPrefix={`stateTaxWithholding-${stateAbbr}`}
                ignore={ignore}
                optionalFields={activeLocation.newFields.optionalFields}
                optionalFieldsMap={OPTIONAL_FIELDS_MAP}
                viewOnly={viewOnly}
            />
        );
    }, [activeLocation, viewOnly, ignore, stateAbbr]);
    
    // Event handlers
    const handleFilingStatusChange = (value: string) => {
        setFilingStatus(activeLocation.newFields.filingStatus.find(status => status.val === value));
    };
    
    const handleIgnoreChange = (checked: boolean) => {
        setIgnore(checked);
    };
    
    return (
        (<SectionStyledBox
            hidden={ showWithholding === false }
            usesSectionComponent={ usesSectionComponent }
        >
            <fieldset>
                <SectionStyledHeader
                    hasPopover={ true }
                    headlineSize={ headerHeadlineSize }
                    popoverContent={ $.__('This determines how much you will withhold from your paycheck for state taxes.') }
                    popoverIcon="circle-question-regular"
                    popoverPlacement="bottom"
                    popoverTitle={ $.__('State Tax Withholding') }
                >
                    { $.__('State Tax Withholding') }
                </SectionStyledHeader>
                { sectionNote ? (
                    <Flex
                        alignItems="center"
                        flexDirection="row"
                        gap={2}
                    >
                        <IconV2
                            color="info-medium"
                            name="circle-info-solid"
                            size={16}
                        />
                        <BodyText
                            color="info-medium"
                            size='small'
                            weight="semibold"
                        >
                            { sectionNote }
                        </BodyText>
                    </Flex>
                ) : null }

                <LayoutBox>
                    <BodyText>
                        { $.__("Please choose your location and filing status, and adjust any additional withholdings if needed. If you make any future changes, be sure to send an updated form to your employer.") }
                    </BodyText>
                    <TextButton
                        data-bi-id="payinfo-taxes-statewh-w4-link"
                        href="https://dam.ldr.la.gov/taxforms/1300(1_25)F.pdf"
                        rel="noopener noreferrer"
                        startIcon={
                            <IconV2
                                color="link"
                                name="arrow-up-right-from-square-regular"
                                size={16}
                            />
                        }
                        target="_blank"
                        type="button"
                    >
                        { $.__('View the State Tax Withholding Form') }
                    </TextButton>
                </LayoutBox>

                <Flex flexDirection="column" gap={2} marginTop={2}>

                    { stateInfo.locations && (
                        <LocationSelect
                            locations={stateInfo.locations}
                            newHirePacket={newHirePacket}
                            onLocationChange={handleLocationChange}
                            selectedLocation={selectedLocation}
                            showLocationRequiredAsterisk={showLocationRequiredAsterisk}
                            stateAbbr={stateAbbr}
                            viewOnly={viewOnly}
                        />
                    ) }

                    { activeLocation && (
                        <>
                            <Flex alignItems="middle" gap={2}>
                                { activeLocation.newFields.filingStatus && (
                                    <FilingStatusSelect
                                        filingStatusOptions={activeLocation.newFields.filingStatus}
                                        ignore={ignore}
                                        onFilingStatusChange={handleFilingStatusChange}
                                        selectedFilingStatus={filingStatus}
                                        viewOnly={viewOnly}
                                    />
                                ) }
                            
                                { activeLocation.newFields.filingStatus && (
                                    <Exemptions
                                        disabled={true} // default to true since it's tied to filing status
                                        ignore={ignore}
                                        label={activeLocation?.newFields?.exemptions?.label}
                                        value={filingStatus ? filingStatus.exemptionsVal : ''}
                                    />
                                ) }
                            </Flex>
                                
                            <Flex alignItems="middle">
                                { feAccessLevelsQuickForms && accessLevel !== undefined ? (
                                    <>
                                        { ((editPermission || viewOnly) && activeLocation.newFields.ignoreStatusExemptions) && (
                                            <Checkbox
                                                checked={ ignore }
                                                disabled={ viewOnly }
                                                id="laStateIgnoreCheckbox"
                                                label={ $.__('Ignore filing status/other exemptions') }
                                                name="state[taxExempt]"
                                                onChange={ (event) => handleIgnoreChange(event.checked) }
                                                value={ ignore ? 'on' : 'off' }
                                            />
                                        ) }
                                    </>
                                ) : (
                                    <>
                                        { ((isAdminUser || isPayrollAdminUser || editPermission) && activeLocation.newFields.ignoreStatusExemptions) && (
                                            <Checkbox
                                                checked={ ignore }
                                                disabled={ viewOnly }
                                                id="laStateIgnoreCheckbox"
                                                label={ $.__('Ignore filing status/other exemptions') }
                                                name="state[taxExempt]"
                                                onChange={ (event) => handleIgnoreChange(event.checked) }
                                                value={ ignore ? 'on' : 'off' }
                                            />
                                        ) }
                                    </>
                                ) }
                            </Flex>

                            { optionalFields }
                            
                            { activeLocation.newFields.additionalWithholding && (
                                <AdditionalWithholding 
                                    data={ activeLocation.newFields.additionalWithholding } 
                                    type="state" 
                                    viewOnly={ viewOnly } 
                                />
                            ) }
                        </>
                    ) }
                </Flex>
            </fieldset>
        </SectionStyledBox>)
    );
}